import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import posthog from 'posthog-js'

import {
    normalizeLandingCode,
    rememberLandingAttribution,
} from 'modules/landing/code'
import {
    APP_DOWNLOAD_URL,
    EITAA_MINIAPP_URL,
    demoPath,
} from 'modules/landing/links'
import downloadMark from 'assets/images/landing/download.png'
import eitaaMark from 'assets/images/landing/eitaa.png'
import LandingSupportChat from 'modules/landing/supportChat'
import { TOPICS } from 'modules/landing/topics'
import { hasSession } from 'utilities/session'

/**
 * The first thing an invited صندوق manager sees — `/q/<code>`.
 *
 * The first screen is one sentence, large, and a button. That is the whole
 * design brief and it came from the owner reading the previous version:
 * *«هیروشات نسخهٔ فعلی به نظرم ۸۰ درصد متن هست اون هم با فونت ریز»* — and,
 * decisively, that reading it drained their own appetite to scroll. A hero
 * that costs effort before it has earned any is a hero that gets closed.
 *
 * So: one claim in type big enough to read at arm's length, one line under it
 * answering the objection that claim raises, one button. Everything else is
 * below the fold, and everything explanatory is inside the tour, said next to
 * the thing it describes.
 *
 * The voice is second-person singular throughout — «صندوقت رو هوشمند کن!»,
 * not «صندوق شما هوشمند می‌گردد». These readers run a fund among people they
 * know. The formal register is how a bank circular addresses a stranger, and
 * mixing the two registers reads worse than either.
 *
 * The hero copy is the owner's own wording, taken verbatim — headline, the
 * line under it and the button label. It names what the product DOES
 * (قرعه‌کشی، وام، اقساط، حساب‌وکتاب) where the previous draft only promised
 * that things would be easier, which is a claim any app could make.
 *
 * The headline does NOT say «توی گوشیت», which an earlier draft did and the
 * owner struck out. Hamyan is an Android app, a PWA and a mini app inside three
 * messengers — a manager doing the month's accounts at a desk is a real user,
 * and a hero that hands them a phone-only promise is wrong about the product on
 * the first line. The 📱 is the owner's and stays: an emoji next to «هوشمند»
 * is decoration, not the claim that the product lives only on a phone.
 *
 * LAYOUT. The hero takes nearly the whole viewport — `dvh`, not `vh`, so the
 * mobile browser bar changing its mind does not leave a gap or a scrollbar —
 * but deliberately not all of it. An earlier version was a full `100dvh` with a
 * bouncing `⌄` under the button, and the owner's reading of it was that the
 * screen was mostly empty. It was: a centred four-element column in a full
 * viewport leaves a large blank below, and an arrow drawn in that blank is a
 * label for the emptiness, not a fix for it. Ending the hero a little short
 * lets the first row of topics peek instead, which invites the scroll by
 * showing what is down there — and costs no vertical space to say so.
 *
 * On a laptop the column widens and the type grows instead of the page staying
 * a phone-shaped strip down the middle of a large screen; the topic list turns
 * into two and then three columns so the width is used rather than padded.
 *
 * The headline sizes with `clamp()` and never wraps. It is one sentence and a
 * sentence broken across two lines mid-phrase reads as two half-thoughts —
 * which was the complaint. `clamp`'s lower bound is set from the longest line
 * this page can hold at 360px, and a test measures the rendered line count at
 * every breakpoint rather than trusting that arithmetic.
 */

export default function LotteryLandingIntro() {
    const { code: codeParam } = useParams()
    const [searchParams] = useSearchParams()
    const navigate = useNavigate()
    const arrivedAt = useRef(Date.now())
    const deepest = useRef(0)

    const code = useMemo(
        () => normalizeLandingCode(codeParam ?? searchParams.get('c')),
        [codeParam, searchParams]
    )

    const track = useCallback(
        (event: string, props: Record<string, unknown> = {}) => {
            posthog.capture(event, { landing_code: code, ...props })
        },
        [code]
    )

    useEffect(() => {
        if (code) rememberLandingAttribution(code)
        posthog.capture('lottery_intro_viewed', {
            landing_code: code,
            has_code: !!code,
            authenticated: hasSession(),
        })
    }, [code])

    // Dwell plus how far down they got. On a page whose whole bet is that a
    // short hero earns the scroll, «چند نفر اصلاً پایین رفتند» is the number
    // that says whether the bet paid — and seconds alone cannot tell somebody
    // who read it from somebody who left it open.
    useEffect(() => {
        const onScroll = () => {
            const doc = document.documentElement
            const max = doc.scrollHeight - window.innerHeight
            if (max <= 0) return
            const pct = Math.min(
                100,
                Math.round(((window.scrollY || doc.scrollTop) / max) * 100)
            )
            if (pct > deepest.current) deepest.current = pct
        }
        window.addEventListener('scroll', onScroll, { passive: true })
        const report = () =>
            posthog.capture('lottery_intro_left', {
                landing_code: code,
                seconds: Math.round((Date.now() - arrivedAt.current) / 1000),
                scroll_depth: deepest.current,
            })
        window.addEventListener('pagehide', report)
        return () => {
            window.removeEventListener('scroll', onScroll)
            window.removeEventListener('pagehide', report)
            report()
        }
    }, [code])

    const openTopic = (topicKey: string, from: string) => {
        track('lottery_intro_topic_opened', { topic: topicKey, from })
        navigate(`${demoPath(code)}?t=${topicKey}`)
    }

    const openExternal = (target: string, url: string) => {
        track('lottery_intro_external_opened', { target })
        window.open(url, '_blank', 'noopener,noreferrer')
    }

    return (
        <div className="min-h-screen bg-white">
            <LandingSupportChat code={code} surface="intro" />

            {/* ------------------------------------------------------- hero */}
            <div
                data-hero
                className="mx-auto flex min-h-[62dvh] w-full max-w-[560px] flex-col justify-center px-[20px] sm:px-[32px] pb-6 pt-14 lg:min-h-[58dvh] lg:max-w-[960px]"
            >
                <span className="inline-block self-start rounded-full bg-brand-soft px-3 py-1 text-xs font-bold text-brand-deep lg:text-sm">
                    صندوق قرعه‌کشی داری؟
                </span>

                {/* Sized against the COLUMN, not the viewport. `7vw` keeps
                    growing after the column has stopped — at 768 the column is
                    still capped at 560 while 7vw is 54px, and a nowrap
                    headline at 54px is 941px of text in 520px of space. The
                    breakpoints below are the same ones the container uses, so
                    the two can no longer drift apart. */}
                <h1 className="mt-5 whitespace-nowrap text-[clamp(24px,7vw,34px)] font-bold leading-[1.45] text-black lg:text-[56px]">
                    صندوقت رو هوشمند کن! 📱
                </h1>

                <p
                    className="mt-4 leading-8 text-text-body lg:leading-10"
                    style={{ fontSize: 'clamp(16px, 2.2vw, 22px)' }}
                >
                    قرعه‌کشی، وام، اقساط و حساب‌وکتاب صندوق، همه توی همیان.
                </p>

                <button
                    type="button"
                    onClick={() => openTopic(TOPICS[0].key, 'hero')}
                    className="mt-8 h-auto w-full rounded-[14px] bg-brand px-[18px] py-4 text-base font-bold leading-7 text-white active:scale-[0.99] sm:w-auto sm:self-start sm:px-10 sm:py-5 sm:text-lg"
                >
                    ببین همیان چطور صندوقت رو راحت‌تر می‌کنه 👇
                </button>
            </div>

            {/* ------------------------------------------------- the index */}
            <div className="mx-auto w-full max-w-[560px] px-[20px] pb-[192px] sm:px-[32px] sm:pb-16 lg:max-w-[960px]">
                <p className="text-lg font-bold text-black">
                    یا از هرکدام که خواستی
                </p>
                <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                    {TOPICS.map((topic) => (
                        <button
                            key={topic.key}
                            type="button"
                            onClick={() => openTopic(topic.key, 'list')}
                            className="flex w-full items-center gap-3 rounded-[14px] border border-divider px-4 py-4 text-right active:bg-[#FAFAFA] hover:border-[#CDE3BF]"
                        >
                            <span
                                aria-hidden
                                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[12px] bg-brand-soft text-lg leading-none"
                            >
                                {topic.icon}
                            </span>
                            <span className="min-w-0 flex-1 whitespace-nowrap text-base font-bold text-black">
                                {topic.title}
                            </span>
                            <span
                                aria-hidden
                                className="shrink-0 text-lg text-text-secondary"
                            >
                                ‹
                            </span>
                        </button>
                    ))}
                </div>

                {/* --------------------------------------------- entrances */}
                {/* Download first. Somebody who has read this far is deciding
                    whether to use Hamyan, not which messenger to open it in —
                    and the app is the fuller answer. */}
                <div className="mt-10 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:mx-auto lg:max-w-[620px]">
                    <button
                        type="button"
                        onClick={() =>
                            openExternal('app_download', APP_DOWNLOAD_URL)
                        }
                        className="flex h-14 items-center justify-center gap-2 rounded-[12px] border border-divider bg-white leading-tight active:scale-[0.99]"
                    >
                        <img
                            src={downloadMark}
                            alt=""
                            aria-hidden
                            className="h-7 w-7 shrink-0"
                        />
                        <span className="text-right leading-tight">
                            <span className="block text-sm font-bold text-black">
                                دریافت اپلیکیشن
                            </span>
                            <span className="block text-[11px] text-text-secondary">
                                بازار، مایکت، گوگل‌پلی
                            </span>
                        </span>
                    </button>
                    <button
                        type="button"
                        onClick={() =>
                            openExternal('eitaa_miniapp', EITAA_MINIAPP_URL)
                        }
                        className="flex h-14 items-center justify-center gap-2 rounded-[12px] border border-divider bg-white text-sm font-bold text-black active:scale-[0.99]"
                    >
                        <img
                            src={eitaaMark}
                            alt=""
                            aria-hidden
                            className="h-7 w-7 shrink-0"
                        />
                        باز کردن در ایتا
                    </button>
                </div>
            </div>
        </div>
    )
}
